import { 
  collection, 
  doc, 
  getDocs, 
  updateDoc, 
  writeBatch,
  serverTimestamp,
  orderBy,
  query
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Staff Service - Manages organizational staff with bulk upload/replacement
 */

/**
 * Required columns for staff upload (exact order)
 */
export const STAFF_REQUIRED_COLUMNS = [
  'first_name',
  'last_name', 
  'email',
  'mobile_phone',
  'role',
  'site_name',
  'department',
  'employee_id',
  'manager_email',
  'country',
  'state',
  'region',
  'next_of_kin_name',
  'next_of_kin_phone',
  'next_of_kin_relationship'
];

/**
 * Validate staff data - minimal validation, accept any text
 */
export const validateStaffData = (staffData) => {
  const errors = [];
  
  staffData.forEach((staff, index) => {
    const rowNum = index + 2; // +2 for header row and 1-based indexing
    
    // Only check that required fields have SOMETHING (any text)
    if (!staff.first_name || staff.first_name.toString().trim() === '') {
      errors.push(`Row ${rowNum}: first_name is required`);
    }
    if (!staff.last_name || staff.last_name.toString().trim() === '') {
      errors.push(`Row ${rowNum}: last_name is required`);
    }
    if (!staff.email || staff.email.toString().trim() === '') {
      errors.push(`Row ${rowNum}: email is required`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Clean staff data - ensure all fields are strings and trim whitespace
 */
const cleanStaffData = (staffData) => {
  return staffData.map(staff => {
    const cleaned = {};
    
    // Convert all fields to strings and trim
    STAFF_REQUIRED_COLUMNS.forEach(column => {
      const value = staff[column];
      cleaned[column] = value ? value.toString().trim() : '';
    });
    
    return cleaned;
  });
};

/**
 * Get current staff for organization
 */
export const getOrganizationStaff = async (orgId) => {
  try {
    console.log('📊 Getting staff for organization:', orgId);
    
    const orgDoc = doc(db, 'c3_organisations', orgId);
    const staffRef = collection(orgDoc, 'staff');
    const q = query(staffRef, orderBy('last_name', 'asc'));
    
    const querySnapshot = await getDocs(q);
    const staff = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('📊 Retrieved staff:', staff.length);
    return staff;
  } catch (error) {
    console.error('Error getting organization staff:', error);
    throw new Error(`Failed to get staff: ${error.message}`);
  }
};

/**
 * Create backup of current staff before replacement
 */
const createStaffBackup = async (orgId, staffData) => {
  try {
    console.log('💾 Creating staff backup for org:', orgId);
    
    const timestamp = new Date().toISOString();
    const backupDoc = doc(db, `c3_organisations/${orgId}/staff_backups`, timestamp);
    
    await updateDoc(backupDoc, {
      backup_data: staffData,
      created_at: serverTimestamp(),
      staff_count: staffData.length
    });
    
    console.log('💾 Staff backup created:', timestamp);
    return timestamp;
  } catch (error) {
    console.error('Error creating staff backup:', error);
    throw error;
  }
};

/**
 * Clean up old staff backups, keep only last 2
 */
const cleanupOldStaffBackups = async (orgId) => {
  try {
    console.log('🧹 Cleaning up old staff backups for org:', orgId);
    
    const backupsRef = collection(db, `c3_organisations/${orgId}/staff_backups`);
    const q = query(backupsRef, orderBy('created_at', 'desc'));
    const snapshot = await getDocs(q);
    
    if (snapshot.docs.length > 2) {
      const batch = writeBatch(db);
      
      // Delete all except the first 2 (most recent)
      snapshot.docs.slice(2).forEach(doc => {
        batch.delete(doc.ref);
      });
      
      await batch.commit();
      console.log('🧹 Cleaned up', snapshot.docs.length - 2, 'old staff backups');
    }
  } catch (error) {
    console.error('Error cleaning up staff backups:', error);
    // Don't throw - cleanup failure shouldn't block upload
  }
};

/**
 * Bulk upload staff with full replacement
 */
export const bulkUploadStaff = async (staffData, orgId) => {
  console.log('📤 Starting bulk staff upload for org:', orgId);
  console.log('📤 Staff data to upload:', staffData.length);
  
  try {
    // 1. Validate data
    const validation = validateStaffData(staffData);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }
    
    // 2. Clean data
    const cleanedData = cleanStaffData(staffData);
    
    // 3. Get current staff for backup
    const currentStaff = await getOrganizationStaff(orgId);
    
    // 4. Create backup of current staff
    if (currentStaff.length > 0) {
      await createStaffBackup(orgId, currentStaff);
    }
    
    // 5. Delete all existing staff
    const batch = writeBatch(db);
    const staffRef = collection(db, `c3_organisations/${orgId}/staff`);
    const existingSnapshot = await getDocs(staffRef);
    
    console.log('🗑️ Deleting', existingSnapshot.docs.length, 'existing staff records');
    existingSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    // 6. Upload new staff
    console.log('📤 Uploading', cleanedData.length, 'new staff records');
    cleanedData.forEach((staff, index) => {
      const staffDoc = doc(staffRef);
      batch.set(staffDoc, {
        ...staff,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        upload_batch: new Date().toISOString()
      });
    });
    
    // 7. Commit all changes atomically
    await batch.commit();
    
    // 8. Update organization document with staff count
    const orgDoc = doc(db, 'c3_organisations', orgId);
    await updateDoc(orgDoc, {
      staff_count: cleanedData.length,
      last_staff_upload: serverTimestamp()
    });
    
    // 9. Cleanup old backups
    await cleanupOldStaffBackups(orgId);
    
    console.log('✅ Staff upload completed successfully');
    
    return {
      successful: cleanedData.length,
      deleted: existingSnapshot.docs.length,
      errors: []
    };
    
  } catch (error) {
    console.error('❌ Staff upload failed:', error);
    throw new Error(`Staff upload failed: ${error.message}`);
  }
};

/**
 * Generate staff upload template with exact column order
 */
export const generateStaffTemplate = () => {
  const headers = STAFF_REQUIRED_COLUMNS.join(',');
  const sampleRow = [
    'John',
    'Smith', 
    'john.smith@company.com',
    '+61400123456',
    'Manager',
    'Sydney Office',
    'Operations',
    'EMP001',
    'jane.doe@company.com',
    'Australia',
    'NSW',
    'APAC',
    'Jane Smith',
    '+61400654321',
    'Spouse'
  ].join(',');
  
  return `${headers}\n${sampleRow}`;
};

/**
 * Export current staff to CSV format
 */
export const exportStaffToCSV = async (orgId) => {
  try {
    const staff = await getOrganizationStaff(orgId);
    
    if (staff.length === 0) {
      return 'No staff data to export';
    }
    
    const headers = STAFF_REQUIRED_COLUMNS.join(',');
    const rows = staff.map(person => {
      return STAFF_REQUIRED_COLUMNS.map(column => 
        person[column] || ''
      ).join(',');
    });
    
    return `${headers}\n${rows.join('\n')}`;
  } catch (error) {
    console.error('Error exporting staff:', error);
    throw new Error(`Failed to export staff: ${error.message}`);
  }
};

const staffService = {
  validateStaffData,
  getOrganizationStaff,
  bulkUploadStaff,
  generateStaffTemplate,
  exportStaffToCSV,
  STAFF_REQUIRED_COLUMNS
};

export default staffService;